(ns ui.view.kanban-opensearch.view
  (:require
   ["react" :as react]
   [clojure.set :as set]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.virtuoso :as v]
   [ui.dom-globals :as dg]
   [ui.view.kanban-opensearch.event :as event]
   [ui.view.kanban-opensearch.model :as model]
   [ui.view.kanban-opensearch.sub :as sub]
   [ui.view.opensearch-cards.view :as cards]))

(def column-scroller
  (react/forwardRef
   (fn [props ref]
     (let [merged-style (js/Object.assign #js {} (or (.-style props) #js {}))
           next-props (js/Object.assign #js {} props)]
       (aset next-props "ref" ref)
       (aset next-props "style" merged-style)
       (react/createElement "div" next-props)))))

(defn- item-id
  [card]
  (or (get card "original.db/id")
      (get card :original.db/id)
      (:db/id card)
      (:id card)))

(defn- slice-values
  "Return a set of column values from `order` between indices [start-idx..end-idx] inclusive.
  Defensive: tolerate nil indices and any `order` seq type."
  [order start-idx end-idx]
  (let [order (if (vector? order) order (vec (or order [])))
        n (count order)
        start-idx (or start-idx 0)
        end-idx (or end-idx 0)
        start-idx (cu/clamp start-idx 0 (max 0 (dec n)))
        end-idx (cu/clamp end-idx 0 (max 0 (dec n)))
        end-excl (min n (inc end-idx))]
    (if (or (zero? n) (<= end-excl start-idx))
      #{}
      (into #{} (subvec order start-idx end-excl)))))

(defn- js-array-cache
  "A function that gives a JS array of a vector. It keeps the last array, so the same
  vector gives the same array at each render, and React does not get a new prop each
  time."
  []
  (let [last-call (volatile! nil)]
    (fn [values]
      (let [[last-values last-array] @last-call]
        (if (= last-values values)
          last-array
          (let [array (into-array values)]
            (vreset! last-call [values array])
            array))))))

(def ^:private height-estimates-array
  (js-array-cache))

(defn- card-placeholder
  []
  [:div
   {:class "tw2-rounded-3xl tw2-border tw2-border-dashed tw2-border-slate-200 tw2-bg-slate-50 tw2-p-6 tw2-text-sm tw2-text-slate-400"}
   "Loading..."])

(defn- collapse-state
  [items collapsed-ids]
  (let [ids (set (keep item-id items))
        collapsed-visible-ids (set/intersection ids (set (or collapsed-ids #{})))]
    (cond
      (empty? ids) :all-expanded
      (empty? collapsed-visible-ids) :all-expanded
      (= (count ids) (count collapsed-visible-ids)) :all-collapsed
      :else :mixed)))

(defn- report-collapse-state!
  [all-visible-items ui-state on-collapse-state-changed]
  (when (fn? on-collapse-state-changed)
    (on-collapse-state-changed
     (collapse-state all-visible-items (:collapsed-ids ui-state)))))

(defn- collapse-state-with-column-collapses
  [all-visible-items ui-state cards-by-column]
  (let [collapsed-item-ids (into (set (or (:collapsed-ids ui-state) #{}))
                                 (mapcat (fn [column-value]
                                           (map item-id (get-in cards-by-column [column-value :items] [])))
                                         (or (:collapsed-columns ui-state) #{})))]
    (collapse-state all-visible-items collapsed-item-ids)))

(defn- visible-column-values
  [ordered-columns columns-lookup hide-empty-columns?]
  (let [ordered-columns (vec (or ordered-columns []))]
    (if hide-empty-columns?
      (->> ordered-columns
           (filter (fn [column-value]
                     (pos? (long (or (get-in columns-lookup [column-value :count]) 0)))))
           vec)
      ordered-columns)))

(defn- card-state-key
  [row-group-value column-value]
  (if (some? row-group-value)
    [row-group-value column-value]
    column-value))

(defn- toggle-collapsed!
  [ui-state* card]
  (when-let [id (item-id card)]
    (swap! ui-state*
           update
           :collapsed-ids
           (fn [collapsed-ids]
             (let [collapsed-ids (or collapsed-ids #{})]
               (if (contains? collapsed-ids id)
                 (disj collapsed-ids id)
                 (conj collapsed-ids id)))))))

(defn- toggle-collapsed-column!
  [ui-state* column-value]
  (swap! ui-state*
         update
         :collapsed-columns
         (fn [collapsed-columns]
           (let [collapsed-columns (or collapsed-columns #{})]
             (if (contains? collapsed-columns column-value)
               (disj collapsed-columns column-value)
               (conj collapsed-columns column-value))))))

(defn- more-cards-to-load?
  "A column loads its next page when it has more cards and a cursor. A page that is
  loading is not loaded again. A page that failed is not loaded again either. The
  list asks for the next page each time it is shown at its end, so a failed request
  came back in a loop when the browser had run out of resources (DEV-8218)."
  [{:keys [has-more? next-cursor status]}]
  (and (true? has-more?)
       (some? next-cursor)
       (not (contains? #{:loading :error} status))))

(defn kanban-column
  [_props]
  (fn [props]
    (let [{:keys [kanban-id card-config column-meta column-value row-group-value cards-by-column on-card-clicked on-selection-changed
                  on-collapse-state-changed all-visible-items ui-state* drag-enabled? card-actions-fn
                  card-status-fn]} props
          card-key (card-state-key row-group-value column-value)
          collapsed-column-key card-key
          cards-sub (rf/subscribe [::sub/cards-in-column kanban-id card-key])
          paging-sub (rf/subscribe [::sub/column-state kanban-id card-key])
          cards (vec (or @cards-sub []))
          paging (or @paging-sub {})
          total (:total paging)
          loaded (count cards)
          header-count (or (:count column-meta) total loaded 0)
          next-cursor (:next-cursor paging)
          can-load-more? (more-cards-to-load? paging)
          virtuoso-total (if can-load-more?
                           (inc loaded)
                           loaded)
          dragging (:dragging @ui-state*)
          drop-target-column (:drop-target-column @ui-state*)
          drop-active? (and dragging
                            (= drop-target-column collapsed-column-key)
                            drag-enabled?)
          selected-ids (:selected-ids @ui-state* #{})
          collapsed-ids (:collapsed-ids @ui-state* #{})
          collapsed-columns (:collapsed-columns @ui-state* #{})
          column-collapsed? (contains? collapsed-columns collapsed-column-key)
          scroller-class "kanban-column-scroller tw2-overflow-y-auto tw2-overflow-x-hidden
            [-ms-overflow-style:auto]
            [scrollbar-color:transparent_transparent]
            [&::-webkit-scrollbar]:tw2-w-[var(--kanban-scrollbar-width)]
            [&::-webkit-scrollbar]:tw2-h-[var(--kanban-scrollbar-width)]
            [&::-webkit-scrollbar-thumb]:tw2-bg-transparent
            [&::-webkit-scrollbar-thumb]:tw2-rounded-full
            [&::-webkit-scrollbar-track]:tw2-bg-transparent
            hover:[scrollbar-color:rgba(148,163,184,0.7)_transparent]
            hover:[&::-webkit-scrollbar-thumb]:tw2-bg-slate-300/70"
          scroller-style {:height "100%"}]
      [:div
       {:class (str "kanban-column tw2-flex tw2-h-full tw2-min-h-0 tw2-w-[320px] tw2-flex-col tw2-rounded-[28px] tw2-border tw2-pt-3 tw2-pl-3 tw2-pb-3 tw2-transition-colors tw2-duration-200 "
                    (cond
                      drop-active?
                      "tw2-bg-slate-50/70 tw2-border-blue-400 tw2-ring-2 tw2-ring-blue-100"
                      :else
                      "tw2-bg-slate-50/70 tw2-border-slate-200"))
        :on-mouse-enter (fn [_]
                          (swap! ui-state* update :hovered-columns (fnil conj #{}) collapsed-column-key))
        :on-mouse-leave (fn [_]
                          (swap! ui-state* update :hovered-columns disj collapsed-column-key))
        :on-drag-over (fn [e]
                        (when drag-enabled?
                          (dg/prevent-default e)
                          (swap! ui-state* assoc :drop-target-column collapsed-column-key)))
        :on-drop (fn [e]
                   (when drag-enabled?
                     (dg/prevent-and-stop e)
                     (let [{:keys [card source-column-value]} (:dragging @ui-state*)]
                       (when (and card
                                  (not= source-column-value column-value))
                         (rf/dispatch [::event/move-card
                                       {:kanban-id kanban-id
                                        :card card
                                        :source-column-value source-column-value
                                        :target-column-value column-value}]))
                       (swap! ui-state* assoc :dragging nil :drop-target-column nil))))}
       [:div
        {:class "tw2-mb-3 tw2-flex tw2-items-center tw2-justify-between tw2-gap-3 tw2-px-1"}
        [:button
         {:type "button"
          :class "tw2-flex tw2-min-w-0 tw2-flex-1 tw2-items-center tw2-justify-between tw2-gap-3 tw2-rounded-lg tw2-text-left hover:tw2-bg-slate-100/70 tw2-px-1 tw2-py-1"
          :on-click (fn [_]
                      (let [next-state (toggle-collapsed-column! ui-state* collapsed-column-key)]
                        (when (fn? on-collapse-state-changed)
                          (on-collapse-state-changed
                           (collapse-state-with-column-collapses all-visible-items
                                                                 next-state
                                                                 cards-by-column)))))}
         [:div {:class "tw2-min-w-0"}
          [:div {:class "tw2-text-sm tw2-font-semibold tw2-text-slate-900 tw2-truncate"}
           (or (:label column-meta) (pr-str column-value))]
          [:div {:class "tw2-text-xs tw2-text-slate-500"}
           (loc/tr-item-count header-count)]]
         [:i.fal.fa-chevron-down
          {:class (str "tw2-text-slate-400 tw2-transition-transform "
                       (when column-collapsed? "-tw2-rotate-90"))}]]]
       (when-not column-collapsed?
         [:div {:class "tw2-min-h-0 tw2-flex-1"}
          [v/virtuoso
           {:style scroller-style
            :className scroller-class
            :components {:Scroller column-scroller}
            :totalCount virtuoso-total
            :overscan 6
            :horizontalDirection false
            :itemContent (fn [index]
                           (let [card (get cards index)
                                 id (item-id card)
                                 load-more-placeholder? (and can-load-more?
                                                             (= index loaded))]
                             (r/as-element
                              (cond
                                card
                                ^{:key (str "card-" index "-" id)}
                                [:div {:class "tw2-pb-3"}
                                 [:div {:draggable (boolean (and drag-enabled? id))
                                        :title (when-not drag-enabled?
                                                 (loc/tr :grid-cell-read-only))
                                        :class (if drag-enabled?
                                                 "tw2-cursor-grab"
                                                 "tw2-cursor-not-allowed tw2-opacity-90")
                                        :on-drag-start (fn [e]
                                                         (if-not (and drag-enabled? id)
                                                           (dg/prevent-and-stop e)
                                                           (do
                                                             (let [dt (dg/data-transfer e)]
                                                               (dg/data-transfer-set-data dt "text/plain" (str id))
                                                               (dg/data-transfer-set-effect-allowed dt "move"))
                                                             (swap! ui-state* assoc
                                                                    :dragging {:card card
                                                                               :source-column-value column-value}
                                                                    :drop-target-column column-value))))
                                        :on-drag-end (fn [_]
                                                       (swap! ui-state* assoc
                                                              :dragging nil
                                                              :drop-target-column nil))}
                                  [cards/render-card {:card card
                                                      :card-config card-config
                                                      :actions (when (fn? card-actions-fn)
                                                                 (card-actions-fn card))
                                                      :status (when (fn? card-status-fn)
                                                                (card-status-fn card))
                                                      :selected? (contains? selected-ids id)
                                                      :collapsed? (contains? collapsed-ids id)
                                                      :on-click (fn [e clicked-card]
                                                                  (cards/select-card! ui-state* clicked-card all-visible-items on-selection-changed e))
                                                      :on-double-click (fn [clicked-card]
                                                                         (when (fn? on-card-clicked)
                                                                           (on-card-clicked clicked-card)))
                                                      :on-toggle-collapse (fn [clicked-card]
                                                                            (let [next-state (toggle-collapsed! ui-state* clicked-card)]
                                                                              (report-collapse-state! all-visible-items
                                                                                                      next-state
                                                                                                      on-collapse-state-changed)))}]]]
                                load-more-placeholder?
                                ^{:key (str "load-more-" index)}
                                [card-placeholder]
                                :else
                                ^{:key (str "empty-" index)}
                                [:div {:style {:height "1px"}}]))))
            :endReached (fn [_last-index]
                          (when can-load-more?
                            (rf/dispatch
                             [::event/load-cards
                              {:kanban-id kanban-id
                               :row-group-value row-group-value
                               :column-value column-value
                               :start-index next-cursor
                               :count (or (:count paging) 50)}])))}]])])))

(defn- kanban-column-strip
  [{:keys [kanban-id card-config cards-by-column columns-lookup ordered-columns row-group-value
           on-card-clicked on-selection-changed on-collapse-state-changed all-visible-items ui-state* drag-enabled?
           card-actions-fn card-status-fn strip-starts*]}]
  (let [n (count ordered-columns)
        buffer 2]
    [v/virtuoso
     {:style {:height "100%" :width "100%"}
      :horizontalDirection true
      :totalCount n
      :overscan 2
      ;; A swimlane that scrolls out of view unmounts its strip. The strip then starts at the
      ;; column that it showed first when it was seen last, and not at the first column.
      :initialTopMostItemIndex (model/strip-start-index
                                {:start        (some-> strip-starts* deref (get row-group-value))
                                 :column-count n})
      :rangeChanged (fn [rng]
                      (let [start (dg/start-index rng)
                            end (dg/end-index rng)
                            visible (slice-values ordered-columns start end)
                            buffered (slice-values ordered-columns (- start buffer) (+ end buffer))]
                        (when strip-starts*
                          (swap! strip-starts* assoc row-group-value start))
                        (rf/dispatch
                         [::event/set-visible-columns
                          {:kanban-id       kanban-id
                           :row-group-value row-group-value
                           :visible-values  visible
                           :buffer-values   buffered}])))
      :itemContent (fn [index]
                     (let [col-value (get ordered-columns index)
                           card-key (card-state-key row-group-value col-value)
                           column-state (get cards-by-column card-key)
                           col-meta (cond-> (get columns-lookup col-value)
                                      (some? row-group-value)
                                      (assoc :count (:total column-state)))]
                       (r/as-element
                        [:div {:class "tw2-h-full mr-4"}
                         ^{:key (str "kanban-column-" (pr-str row-group-value) "-" (pr-str col-value))}
                         [kanban-column {:kanban-id kanban-id
                                         :card-config card-config
                                         :column-meta col-meta
                                         :column-value col-value
                                         :row-group-value row-group-value
                                         :cards-by-column cards-by-column
                                         :on-card-clicked on-card-clicked
                                         :on-selection-changed on-selection-changed
                                         :on-collapse-state-changed on-collapse-state-changed
                                         :all-visible-items all-visible-items
                                         :ui-state* ui-state*
                                         :drag-enabled? drag-enabled?
                                         :card-actions-fn card-actions-fn
                                         :card-status-fn card-status-fn}]])))}]))

(defn- kanban-row-group
  "One swimlane. A swimlane with cards has a strip of columns, and the strip reports
  the columns that it shows. An empty swimlane is a header only: it has no strip and
  loads nothing."
  [{:keys [row-value row-meta strip-props]}]
  (let [row-label (or (:label row-meta)
                      (some-> row-value pr-str)
                      (loc/tr :uncategorized))
        row-count (or (:count row-meta) 0)
        header (fn [class]
                 [:div {:class class}
                  [:h3 {:class "tw2-text-sm tw2-font-semibold tw2-text-slate-900"}
                   row-label]
                  [:span {:class "tw2-text-xs tw2-text-slate-500"}
                   (loc/tr-item-count row-count)]])]
    (if (model/row-group-empty? row-meta)
      [:section {:class "tw2-rounded-[18px] tw2-border tw2-border-slate-200 tw2-bg-white tw2-px-3 tw2-py-2"}
       (header "tw2-flex tw2-items-baseline tw2-gap-2 tw2-px-1")]
      [:section {:class "tw2-min-h-[360px] tw2-rounded-[18px] tw2-border tw2-border-slate-200 tw2-bg-white tw2-p-3"}
       (header "tw2-mb-3 tw2-flex tw2-items-baseline tw2-gap-2 tw2-px-1")
       [:div {:class "tw2-h-[300px] tw2-min-h-0"}
        [kanban-column-strip (assoc strip-props :row-group-value row-value)]]])))

(defn kanban-board
  [{:keys [kanban-id]}]
  (let [columns-order (rf/subscribe [::sub/column-order kanban-id])
        columns-by-val (rf/subscribe [::sub/column-by-value kanban-id])
        row-groups-order (rf/subscribe [::sub/row-group-order kanban-id])
        row-groups-by-val (rf/subscribe [::sub/row-group-by-value kanban-id])
        ;; The first column of each strip that was seen last. A plain atom: a write on each scroll
        ;; must not render anything.
        strip-starts* (atom {})]
    (fn [props]
      (let [{:keys [card-config on-card-clicked on-selection-changed on-collapse-state-changed
                    all-visible-items ui-state* drag-enabled? cards-by-column hide-empty-columns? row-group-by-field
                    card-actions-fn card-status-fn]} props
            columns-lookup (or @columns-by-val {})
            row-groups-lookup (or @row-groups-by-val {})
            ordered-columns (visible-column-values @columns-order columns-lookup hide-empty-columns?)
            row-grouped? (some? row-group-by-field)
            ordered-row-groups (if row-grouped?
                                 (vec (or @row-groups-order []))
                                 [nil])
            total-items (if row-grouped?
                          (reduce (fn [acc row-value]
                                    (+ acc (or (get-in row-groups-lookup [row-value :count]) 0)))
                                  0
                                  ordered-row-groups)
                          (reduce (fn [acc column-value]
                                    (+ acc (or (get-in columns-lookup [column-value :count]) 0)))
                                  0
                                  ordered-columns))]
        (cond
          (or (zero? (count ordered-columns))
              (zero? (count ordered-row-groups))
              (zero? total-items))
          [:div {:class "tw2-flex tw2-h-full tw2-min-h-0 tw2-w-full tw2-items-center tw2-justify-center tw2-px-4 tw2-text-sm tw2-text-slate-400"}
           (loc/tr :grid-no-entities-found)]

          row-grouped?
          (let [strip-props {:kanban-id                 kanban-id
                             :card-config               card-config
                             :cards-by-column           cards-by-column
                             :columns-lookup            columns-lookup
                             :ordered-columns           ordered-columns
                             :on-card-clicked           on-card-clicked
                             :on-selection-changed      on-selection-changed
                             :on-collapse-state-changed on-collapse-state-changed
                             :all-visible-items         all-visible-items
                             :ui-state*                 ui-state*
                             :drag-enabled?             drag-enabled?
                             :card-actions-fn           card-actions-fn
                             :card-status-fn            card-status-fn
                             :strip-starts*             strip-starts*}
                row-buffer 1]
            ;; The swimlanes are a vertical list. Only the swimlanes that are in or near view
            ;; are shown, and only their strips of columns load cards.
            [:div {:class "tw2-h-full tw2-min-h-0 tw2-w-full"}
             [v/virtuoso
              {:style             {:height "100%" :width "100%"}
               :className         "tw2-pr-2"
               :totalCount        (count ordered-row-groups)
               ;; No `:defaultItemHeight`: it makes Virtuoso skip the estimates.
               :heightEstimates   (height-estimates-array
                                   (model/row-group-height-estimates
                                    {:ordered-row-groups ordered-row-groups
                                     :row-groups-lookup  row-groups-lookup}))
               :overscan          200
               :rangeChanged      (fn [rng]
                                    (let [start (dg/start-index rng)
                                          end (dg/end-index rng)
                                          visible (slice-values ordered-row-groups start end)
                                          buffered (slice-values ordered-row-groups
                                                                 (- start row-buffer)
                                                                 (+ end row-buffer))]
                                      (rf/dispatch
                                       [::event/set-visible-rows
                                        {:kanban-id      kanban-id
                                         :visible-values visible
                                         :buffer-values  buffered}])))
               :itemContent       (fn [index]
                                    (let [row-value (get ordered-row-groups index)]
                                      (r/as-element
                                       [:div {:class "tw2-pb-4"}
                                        ^{:key (str "kanban-row-group-" (pr-str row-value))}
                                        [kanban-row-group {:row-value   row-value
                                                           :row-meta    (get row-groups-lookup row-value)
                                                           :strip-props strip-props}]])))}]])

          :else
          [:div
           {:class "tw2-h-full tw2-min-h-0 tw2-w-full tw2-overflow-hidden"}
           [kanban-column-strip {:kanban-id kanban-id
                                 :card-config card-config
                                 :cards-by-column cards-by-column
                                 :columns-lookup columns-lookup
                                 :ordered-columns ordered-columns
                                 :row-group-value nil
                                 :on-card-clicked on-card-clicked
                                 :on-selection-changed on-selection-changed
                                 :on-collapse-state-changed on-collapse-state-changed
                                 :all-visible-items all-visible-items
                                 :ui-state* ui-state*
                                 :drag-enabled? drag-enabled?
                                 :card-actions-fn card-actions-fn
                                 :card-status-fn card-status-fn}]])))))

(defn view
  [{:keys [kanban-id] :as props}]
  (let [ui-state* (r/atom {:selected-ids #{}
                           :collapsed-ids #{}
                           :collapsed-columns #{}
                           :last-selected-index nil})
        last-collapse-command-id* (r/atom nil)]
    (r/create-class
     {:component-did-mount
      (fn [_]
        (rf/dispatch [::event/init props]))

      :component-did-update
      (fn [this old-argv]
        (let [old-props (second old-argv)
              new-props (r/props this)
              old-config (:config old-props)
              new-config (:config new-props)]
          (when (not= old-config new-config)
            (reset! last-collapse-command-id* nil)
            (reset! ui-state* {:selected-ids #{}
                               :collapsed-ids #{}
                               :collapsed-columns #{}
                               :last-selected-index nil})
            (rf/dispatch [::event/init new-props]))))

      :component-will-unmount
      (fn [_]
        (rf/dispatch [::event/destroy {:kanban-id kanban-id}]))

      :reagent-render
      (fn [{:keys [kanban-id collapse-command on-card-clicked on-selection-changed on-collapse-state-changed
                   card-actions-fn card-status-fn] :as _props}]
        (let [config @(rf/subscribe [::sub/config kanban-id])
              group-by-field (:group-by-field config)
              row-group-by-field (:row-group-by-field config)
              has-group-by-field? (some? group-by-field)
              card-config @(rf/subscribe [::sub/card-config kanban-id])
              cards-by-column @(rf/subscribe [::sub/cards-by-column kanban-id])
              grouping-draggable? @(rf/subscribe [::sub/grouping-draggable? kanban-id])
              read-only-user? (session/is-read-only-user?)
              drag-enabled? (and (not read-only-user?)
                                 grouping-draggable?)
              columns-status @(rf/subscribe [::sub/columns-status kanban-id])
              row-groups-status @(rf/subscribe [::sub/row-groups-status kanban-id])
              initialized? (and (some? card-config)
                                (not= columns-status :idle)
                                (or (nil? row-group-by-field)
                                    (not= row-groups-status :idle)))
              show-read-only-hint? (and has-group-by-field?
                                        (not drag-enabled?))
              all-visible-items (->> (vals (or cards-by-column {}))
                                     (mapcat (fn [column-state]
                                               (or (:items column-state) [])))
                                     vec)
              collapse-command-id (:id collapse-command)]
          (when (and collapse-command-id
                     (not= collapse-command-id @last-collapse-command-id*))
            (reset! last-collapse-command-id* collapse-command-id)
            (case (:action collapse-command)
              :collapse-all
              (swap! ui-state* assoc :collapsed-ids (into #{} (keep item-id all-visible-items)))

              :expand-all
              (swap! ui-state* assoc
                     :collapsed-ids #{}
                     :collapsed-columns #{})

              nil))
          [:div
           {:class "flex flex-auto min-h-0 flex-col gap-3"}

           (when show-read-only-hint?
             [:div
              {:class "text-xs text-base-light-text-paler"}
              (loc/tr :grid-cell-read-only)])

           [:div
            {:class "flex flex-auto min-h-0"}
            (cond
              ;; `config` arrives from ::event/init after mount, so it is nil on
              ;; the first renders. Judging group-by before it lands reported a
              ;; slow load as a broken listing - and the loading branch below
              ;; could never cover that window, because `initialized?` needs
              ;; card-config, which only exists once config already does.
              (and (some? config)
                   (not has-group-by-field?))
              [:div {:class "px-4 py-6 text-sm text-base-light-text-paler"}
               (loc/tr :kanban-not-configured-group-by-missing)]

              (not initialized?)
              [:div {:class "px-4 py-6 text-sm text-base-light-text-paler"}
               (loc/tr :kanban-loading-cards)]

              :else
              ^{:key (str "kanban-board-" (hash card-config))}
              [kanban-board {:kanban-id kanban-id
                             :cards-by-column cards-by-column
                             :hide-empty-columns? (:hide-empty-columns? config)
                             :row-group-by-field row-group-by-field
                             :card-config card-config
                             :on-card-clicked on-card-clicked
                             :on-selection-changed on-selection-changed
                             :on-collapse-state-changed on-collapse-state-changed
                             :all-visible-items all-visible-items
                             :ui-state* ui-state*
                             :drag-enabled? drag-enabled?
                             :card-actions-fn card-actions-fn
                             :card-status-fn card-status-fn}])]]))})))
